/**
 * CLIENTELE — Product Page JavaScript
 * assets/clientele-product.js
 *
 * Handles:
 * - Thumbnail gallery switching
 * - Hover zoom on main image
 * - Variant selection + URL update + price/stock update
 * - Accordion open/close with height animation
 * - Sticky ATC bar on scroll
 * - Size guide modal (inline slide-in panel)
 * - ATC form submission with silent cart add (no popups)
 */

(function() {
  'use strict';

  // ── Utility ────────────────────────────────────────
  function qs(selector, ctx) {
    return (ctx || document).querySelector(selector);
  }
  function qsa(selector, ctx) {
    return Array.from((ctx || document).querySelectorAll(selector));
  }
  function money(price) {
    // Shopify Liquid `product.variants | json` (storefront) emits price as
    // INTEGER CENTS (e.g. 9000 for $90.00). Decimal-string dollar values
    // (e.g. "90.00") only come from the predictive-search API — handle both:
    // no decimal point = cents, decimal point = dollars.
    var n = parseFloat(price);
    if (isNaN(n)) return '';
    if (String(price).indexOf('.') === -1) n = n / 100;
    return '$' + n.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  // ── Find section root ───────────────────────────────
  document.querySelectorAll('.c-product-section').forEach(function(section) {
    initProductSection(section);
  });

  function initProductSection(section) {
    var sid = section.querySelector('[data-section]').dataset.section;

    // ══════════════════════════════════════════════════
    // 1. GALLERY
    // ══════════════════════════════════════════════════
    var galleryMain  = qs('#c-gallery-main-' + sid, section);
    var thumbsEl     = qs('#c-thumbs-' + sid, section);
    var mediaItems   = qsa('.c-product__media-item', section);
    var thumbBtns    = qsa('.c-product__thumb', section);
    var currentIndex = 0;

    function goToMedia(index) {
      if (index === currentIndex && mediaItems[index].classList.contains('is-active')) return;
      mediaItems.forEach(function(m, i) {
        m.classList.toggle('is-active', i === index);
      });
      thumbBtns.forEach(function(t, i) {
        t.classList.toggle('is-active', i === index);
        t.setAttribute('aria-current', i === index ? 'true' : 'false');
      });
      currentIndex = index;
    }

    thumbBtns.forEach(function(btn) {
      btn.addEventListener('click', function() {
        goToMedia(parseInt(btn.dataset.thumbIndex, 10));
      });
    });

    // ── Image zoom on click ─────────────────────────
    if (galleryMain) {
      galleryMain.addEventListener('click', function(e) {
        var activeImg = qs('.c-product__media-item.is-active .c-product__media-img', section);
        if (!activeImg) return;
        if (galleryMain.classList.contains('is-zoomed')) {
          galleryMain.classList.remove('is-zoomed');
        } else {
          galleryMain.classList.add('is-zoomed');
        }
      });

      // Track mouse position for zoom origin
      galleryMain.addEventListener('mousemove', function(e) {
        if (!galleryMain.classList.contains('is-zoomed')) return;
        var rect = galleryMain.getBoundingClientRect();
        var x = ((e.clientX - rect.left) / rect.width * 100).toFixed(2) + '%';
        var y = ((e.clientY - rect.top) / rect.height * 100).toFixed(2) + '%';
        galleryMain.style.setProperty('--zoom-x', x);
        galleryMain.style.setProperty('--zoom-y', y);
      });

      galleryMain.addEventListener('mouseleave', function() {
        galleryMain.classList.remove('is-zoomed');
      });
    }

    // ══════════════════════════════════════════════════
    // 2. VARIANT SELECTION
    // ══════════════════════════════════════════════════
    var productDataEl  = section.querySelector('[data-product-id]');
    var productId      = productDataEl ? productDataEl.dataset.productId : null;
    var variantIdInput = qs('#c-variant-id-' + sid, section);
    var priceWrap      = qs('#c-price-' + sid, section);
    var lowStockEl     = qs('#c-low-stock-' + sid, section);
    var atcBtn         = qs('#c-atc-btn-' + sid, section);
    var stickyVariant  = qs('#c-sticky-variant-' + sid, section);
    var stickyAtcBtn   = qs('#c-sticky-atc-btn-' + sid, section);

    var sizeBtns = qsa('.c-product__size-btn', section);
    var selectedOptions = {};

    // Init selected options from checked inputs
    sizeBtns.forEach(function(btn) {
      var input = btn.querySelector('input');
      if (input && input.checked) {
        selectedOptions[btn.dataset.option] = btn.dataset.value;
      }
    });

    sizeBtns.forEach(function(btn) {
      btn.addEventListener('click', function() {
        var optionName = btn.dataset.option;
        var optionValue = btn.dataset.value;
        if (btn.classList.contains('is-sold-out')) return;

        // Deselect same option group
        qsa('.c-product__size-btn[data-option="' + optionName + '"]', section).forEach(function(b) {
          b.classList.remove('is-selected');
          var i = b.querySelector('input');
          if (i) i.checked = false;
        });

        btn.classList.add('is-selected');
        var inp = btn.querySelector('input');
        if (inp) inp.checked = true;

        selectedOptions[optionName] = optionValue;
        updateVariant();
      });
    });

    function updateVariant() {
      if (!window.__clientele_variants) return;
      var variants = window.__clientele_variants[productId];
      if (!variants) return;

      // Find matching variant
      var matched = null;
      variants.forEach(function(v) {
        var match = true;
        Object.keys(selectedOptions).forEach(function(opt) {
          if (opt === 'size' || opt === 'sizes') {
            if (v.option1 !== selectedOptions[opt] &&
                v.option2 !== selectedOptions[opt] &&
                v.option3 !== selectedOptions[opt]) match = false;
          }
        });
        if (match && !matched) matched = v;
      });

      if (!matched) return;

      // Update hidden input
      if (variantIdInput) variantIdInput.value = matched.id;

      // Update price
      if (priceWrap) {
        if (matched.compare_at_price > matched.price) {
          priceWrap.innerHTML =
            '<span class="c-product__price c-product__price--sale">' + money(matched.price) + '</span>' +
            '<span class="c-product__price c-product__price--compare">' + money(matched.compare_at_price) + '</span>';
        } else {
          priceWrap.innerHTML = '<span class="c-product__price">' + money(matched.price) + '</span>';
        }
      }

      // Update low stock
      if (lowStockEl) {
        var threshold = parseInt(section.querySelector('[id^="c-product-"]').dataset.lowStock || '4', 10);
        if (matched.inventory_management === 'shopify' && matched.inventory_quantity > 0 && matched.inventory_quantity <= 4) {
          lowStockEl.textContent = 'ONLY ' + matched.inventory_quantity + ' LEFT';
          lowStockEl.classList.remove('c-product__low-stock--hidden');
        } else {
          lowStockEl.textContent = '';
          lowStockEl.classList.add('c-product__low-stock--hidden');
        }
      }

      // Update ATC button state
      if (atcBtn) {
        if (matched.available) {
          atcBtn.disabled = false;
          atcBtn.classList.remove('c-product__atc-btn--sold-out');
          qs('.c-product__atc-text', atcBtn).textContent = 'ADD TO BAG';
        } else {
          atcBtn.classList.add('c-product__atc-btn--sold-out');
          qs('.c-product__atc-text', atcBtn).textContent = 'NOTIFY ME';
        }
      }

      // Update sticky
      if (stickyVariant && matched.title !== 'Default Title') {
        stickyVariant.textContent = matched.title;
      }
      if (stickyAtcBtn) {
        stickyAtcBtn.classList.toggle('is-sold-out', !matched.available);
        stickyAtcBtn.textContent = matched.available ? 'ADD TO BAG' : 'SOLD OUT';
      }

      // Update gallery if variant has image
      if (matched.featured_image) {
        var mediaId = matched.featured_image.id;
        var targetIndex = mediaItems.findIndex(function(m) {
          return m.dataset.mediaId == mediaId;
        });
        if (targetIndex >= 0) goToMedia(targetIndex);
      }

      // Update URL
      var url = new URL(window.location.href);
      url.searchParams.set('variant', matched.id);
      window.history.replaceState({ variantId: matched.id }, '', url.toString());
    }

    // ══════════════════════════════════════════════════
    // 3. ADD TO CART (silent — no confetti, no popup)
    // ══════════════════════════════════════════════════
    var productForm = qs('.c-product__form', section);
    if (productForm) {
      productForm.addEventListener('submit', function(e) {
        e.preventDefault();
        if (!atcBtn || atcBtn.classList.contains('c-product__atc-btn--sold-out')) return;

        atcBtn.classList.add('is-loading');
        atcBtn.disabled = true;

        var formData = new FormData(productForm);
        var variantId = formData.get('id');

        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: variantId, quantity: 1 })
        })
        .then(function(res) { return res.json(); })
        .then(function(item) {
          atcBtn.classList.remove('is-loading');
          atcBtn.classList.add('is-added');
          qs('.c-product__atc-text', atcBtn).textContent = 'ADDED TO BAG';

          // Update cart count in nav if present
          fetch('/cart.js')
            .then(function(r) { return r.json(); })
            .then(function(cart) {
              var countEls = document.querySelectorAll('[data-cart-count], .cart-count-bubble, .cart-icon-bubble');
              countEls.forEach(function(el) {
                el.textContent = cart.item_count;
              });
            });

          // Reset after 2.5s
          setTimeout(function() {
            atcBtn.classList.remove('is-added');
            qs('.c-product__atc-text', atcBtn).textContent = 'ADD TO BAG';
            atcBtn.disabled = false;
          }, 2500);
        })
        .catch(function() {
          atcBtn.classList.remove('is-loading');
          atcBtn.disabled = false;
          qs('.c-product__atc-text', atcBtn).textContent = 'TRY AGAIN';
          setTimeout(function() {
            qs('.c-product__atc-text', atcBtn).textContent = 'ADD TO BAG';
          }, 2000);
        });
      });
    }

    // Sticky ATC button
    if (stickyAtcBtn && productForm) {
      stickyAtcBtn.addEventListener('click', function() {
        if (stickyAtcBtn.classList.contains('is-sold-out')) return;
        productForm.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
      });
    }

    // ══════════════════════════════════════════════════
    // 4. STICKY ATC BAR
    // ══════════════════════════════════════════════════
    var stickyBar = qs('#c-sticky-atc-' + sid);
    var titleEl   = qs('#c-product-title-' + sid, section);

    if (stickyBar && titleEl) {
      var titleObserver = new IntersectionObserver(function(entries) {
        entries.forEach(function(entry) {
          if (!entry.isIntersecting) {
            stickyBar.classList.add('is-visible');
            stickyBar.removeAttribute('aria-hidden');
          } else {
            stickyBar.classList.remove('is-visible');
            stickyBar.setAttribute('aria-hidden', 'true');
          }
        });
      }, { threshold: 0, rootMargin: '-80px 0px 0px 0px' });

      titleObserver.observe(titleEl);
    }

    // ══════════════════════════════════════════════════
    // 5. ACCORDION
    // ══════════════════════════════════════════════════
    qsa('[data-accordion]', section).forEach(function(acc) {
      var trigger = qs('.c-product__accordion-trigger', acc);
      var body    = qs('.c-product__accordion-body', acc);
      var icon    = qs('.c-product__accordion-icon', acc);
      if (!trigger || !body) return;

      trigger.addEventListener('click', function() {
        var isOpen = trigger.getAttribute('aria-expanded') === 'true';

        if (isOpen) {
          // Close
          body.style.height = body.scrollHeight + 'px';
          requestAnimationFrame(function() {
            body.style.height = '0';
            body.style.overflow = 'hidden';
          });
          trigger.setAttribute('aria-expanded', 'false');
          if (icon) icon.textContent = '+';
          setTimeout(function() {
            body.hidden = true;
            body.style.height = '';
            body.style.overflow = '';
          }, 320);
        } else {
          // Open
          body.hidden = false;
          body.style.height = '0';
          body.style.overflow = 'hidden';
          var targetHeight = body.scrollHeight + 'px';
          requestAnimationFrame(function() {
            body.style.height = targetHeight;
          });
          trigger.setAttribute('aria-expanded', 'true');
          if (icon) icon.textContent = '−';
          setTimeout(function() {
            body.style.height = '';
            body.style.overflow = '';
          }, 320);
        }
      });
    });

    // ══════════════════════════════════════════════════
    // 6. SIZE GUIDE MODAL
    // ══════════════════════════════════════════════════
    var sizeGuideModal   = qs('#c-size-guide-' + sid);
    var sizeGuideTrigger = qs('.c-product__size-guide-trigger', section);

    if (sizeGuideModal && sizeGuideTrigger) {
      sizeGuideTrigger.addEventListener('click', function() {
        openModal(sizeGuideModal);
      });

      // Close on backdrop or X button
      sizeGuideModal.querySelectorAll('[data-close-modal]').forEach(function(el) {
        el.addEventListener('click', function() {
          closeModal(sizeGuideModal);
        });
      });

      // Close on Escape
      document.addEventListener('keydown', function(e) {
        if (e.key === 'Escape' && !sizeGuideModal.hidden) {
          closeModal(sizeGuideModal);
          sizeGuideTrigger.focus();
        }
      });
    }

    function openModal(modal) {
      modal.hidden = false;
      document.body.style.overflow = 'hidden';
      var panel = modal.querySelector('.c-size-guide-modal__panel');
      if (panel) {
        var firstFocusable = panel.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
        if (firstFocusable) firstFocusable.focus();
      }
    }

    function closeModal(modal) {
      modal.hidden = true;
      document.body.style.overflow = '';
    }
  }

  // ══════════════════════════════════════════════════
  // VARIANT DATA — injected by Liquid into page
  // (The section .liquid file injects this via a script tag)
  // We read it from the window object
  // ══════════════════════════════════════════════════

})();

/* ═══════════════════════════════════════════════════════════════
   MODULE 11 — ANALYTICS INFRASTRUCTURE (Phase 2)
   GA4 Enhanced Ecommerce events + Meta Pixel events
   Fires on: page view, product view, add-to-cart, form success
═══════════════════════════════════════════════════════════════ */
(function() {
  'use strict';

  // ── GA4 helper ────────────────────────────────────────────
  function gaSend(eventName, params) {
    if (typeof gtag !== 'function') return;
    try {
      gtag('event', eventName, params);
    } catch(e) {}
  }

  // ── Meta Pixel helper ─────────────────────────────────────
  function pixelSend(eventName, data) {
    if (typeof fbq !== 'function') return;
    try {
      fbq('track', eventName, data);
    } catch(e) {}
  }

  // ── Product View (ViewItem) ────────────────────────────────
  // Fires once on DOMContentLoaded for product pages
  document.addEventListener('DOMContentLoaded', function() {
    var productSection = document.querySelector('.c-product-section');
    if (!productSection) return;

    var productId = productSection.querySelector('[data-product-id]');
    var productTitle = document.querySelector('.c-product__title');
    var priceEl = document.querySelector('.c-product__price');

    var itemId   = productId ? productId.getAttribute('data-product-id') : '';
    var itemName = productTitle ? productTitle.textContent.trim() : document.title;
    var priceTxt = priceEl ? priceEl.textContent.replace(/[^0-9.]/g, '') : '0';
    var priceVal = parseFloat(priceTxt) || 0;

    // GA4 view_item
    gaSend('view_item', {
      currency: 'USD',
      value: priceVal,
      items: [{
        item_id: itemId,
        item_name: itemName,
        item_brand: document.querySelector('.c-product__label') ? document.querySelector('.c-product__label').textContent.trim() : '',
        price: priceVal,
        quantity: 1
      }]
    });

    // Meta Pixel ViewContent
    pixelSend('ViewContent', {
      content_ids: [itemId],
      content_name: itemName,
      content_type: 'product',
      value: priceVal,
      currency: 'USD'
    });
  });

  // ── Add To Cart event ──────────────────────────────────────
  // Listen for Shopify cart:add events OR our ATC button click
  document.addEventListener('DOMContentLoaded', function() {
    document.querySelectorAll('[data-type="add-to-cart-form"]').forEach(function(form) {
      form.addEventListener('submit', function() {
        var titleEl  = document.querySelector('.c-product__title');
        var priceEl  = document.querySelector('.c-product__price');
        var section  = form.closest('[data-product-id]');
        var itemId   = section ? section.getAttribute('data-product-id') : '';
        var itemName = titleEl ? titleEl.textContent.trim() : '';
        var priceTxt = priceEl ? priceEl.textContent.replace(/[^0-9.]/g, '') : '0';
        var priceVal = parseFloat(priceTxt) || 0;

        // GA4 add_to_cart
        gaSend('add_to_cart', {
          currency: 'USD',
          value: priceVal,
          items: [{
            item_id: itemId,
            item_name: itemName,
            price: priceVal,
            quantity: 1
          }]
        });

        // Meta Pixel AddToCart
        pixelSend('AddToCart', {
          content_ids: [itemId],
          content_name: itemName,
          content_type: 'product',
          value: priceVal,
          currency: 'USD'
        });
      });
    });
  });

  // ── Wishlist Save event ────────────────────────────────────
  document.addEventListener('DOMContentLoaded', function() {
    document.querySelectorAll('[data-wishlist-save]').forEach(function(btn) {
      btn.addEventListener('click', function() {
        var wasPressed = btn.getAttribute('aria-pressed') === 'true';
        if (!wasPressed) {
          // Just saved — fire analytics
          var itemId   = btn.getAttribute('data-product-id') || '';
          var itemName = btn.getAttribute('data-product-title') || '';
          var priceRaw = btn.getAttribute('data-product-price') || '0';
          var priceVal = parseFloat(priceRaw) || 0;
          if (priceRaw.indexOf('.') === -1) priceVal = priceVal / 100; // integer = Liquid cents

          gaSend('add_to_wishlist', {
            currency: 'USD',
            value: priceVal,
            items: [{
              item_id: itemId,
              item_name: itemName,
              price: priceVal,
              quantity: 1
            }]
          });
        }
      });
    });
  });

  // ── Consignment Form Submit ────────────────────────────────
  document.addEventListener('DOMContentLoaded', function() {
    var sellForm = document.getElementById('c-sell-form');
    if (sellForm) {
      sellForm.addEventListener('submit', function() {
        gaSend('generate_lead', {
          currency: 'USD',
          value: 0,
          lead_source: 'consignment_form'
        });
        pixelSend('Lead', {
          content_name: 'Consignment Inquiry'
        });
      });
    }
  });

  // ── Notify Capture (restock) ───────────────────────────────
  document.addEventListener('DOMContentLoaded', function() {
    document.querySelectorAll('[data-notify-form]').forEach(function(form) {
      form.addEventListener('submit', function() {
        var titleEl = document.querySelector('.c-product__title');
        gaSend('notify_restock', {
          item_name: titleEl ? titleEl.textContent.trim() : 'Unknown Product'
        });
      });
    });
  });

})();
/* ── END MODULE 11 ──────────────────────────────────────── */
